"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

import ImageAttribution from "@/components/image-attribution";
import type { GalleryItem } from "@/types/gallery";

const MD_QUERY = "(min-width: 768px)"; // Tailwind 'md'
const COUNT_BY_BREAKPOINT = {
  base: 2,
  md: 4,
} as const;

function getCountForBreakpoint(isMdUp: boolean) {
  return isMdUp ? COUNT_BY_BREAKPOINT.md : COUNT_BY_BREAKPOINT.base;
}

export default function BrasilGallery() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [requestedCount, setRequestedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MD_QUERY);

    const updateCount = (event?: MediaQueryListEvent) => {
      const matches = event ? event.matches : mediaQuery.matches;
      setRequestedCount(getCountForBreakpoint(matches));
    };

    updateCount();

    const listener = (event: MediaQueryListEvent) => updateCount(event);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", listener);
      return () => mediaQuery.removeEventListener("change", listener);
    }

    mediaQuery.addListener(listener);
    return () => mediaQuery.removeListener(listener);
  }, []);

  useEffect(() => {
    if (!requestedCount) {
      return;
    }

    const controller = new AbortController();
    const fetchGallery = async () => {
      try {
        const response = await fetch(`/api/brasil-gallery?count=${requestedCount}`, {
          cache: "no-cache",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data: { items: GalleryItem[] } = await response.json();
        setItems(data.items);
        setError(null);
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          return;
        }
        setError("We could not load the gallery right now. Please try again.");
        setItems([]);
      }
    };

    fetchGallery();

    return () => {
      controller.abort();
    };
  }, [requestedCount]);

  if (error) {
    return (
      <div className="mt-10">
        <p className="rounded-3xl border border-rose-200 bg-rose-50 px-4 py-6 text-sm text-rose-700">
          {error}
        </p>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="mt-10">
        <p className="rounded-3xl border border-emerald-100 bg-white px-4 py-6 text-sm text-emerald-800">
          Loading culture highlights…
        </p>
      </div>
    );
  }

  return (
    <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {items.map(({ title, caption, filename, attrib }) => (
        <figure
          className="flex flex-col overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm"
          key={title}
        >
          <Image
            alt={title}
            className="h-full w-full object-cover"
            height={475}
            src={`${process.env.NEXT_PUBLIC_SPT_IMAGE_HOST}/${filename}`}
            width={700}
          />
          <figcaption className="px-4 py-4 text-sm text-slate-500">
            {caption} <ImageAttribution attrib={attrib} />
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
